import { useEffect, useRef, useState } from 'react'
import { Card, Chip, inputClass, primaryBtn } from '../family-finance/shared'
import type { AssistantMessage } from '../../types/family-planner'
import { askAssistant, isPlannerOffline } from '../../lib/family-planner-api'
import type { PlannerState } from './usePlanner'

const STARTERS = [
  'What is on this weekend?',
  'Any conflicts coming up?',
  'When are we free for 4 nights?',
  'Show me travel deals',
  "What does Meagan have next week?",
]

/** Minimal renderer for the **bold** + bullet-line format the answers use. */
function AnswerText({ text }: { text: string }) {
  return (
    <div className="space-y-1">
      {text.split('\n').map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />
        const parts = line.split(/(\*\*[^*]+\*\*)/g)
        return (
          <p key={i} className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            {parts.map((part, j) =>
              part.startsWith('**') && part.endsWith('**') ? (
                <strong key={j} className="text-slate-900 dark:text-white font-semibold">
                  {part.slice(2, -2)}
                </strong>
              ) : (
                <span key={j}>{part}</span>
              ),
            )}
          </p>
        )
      })}
    </div>
  )
}

export default function AssistantTab({ state }: { state: PlannerState }) {
  const { assistantContext, events, windows, conflicts } = state
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages, thinking])

  async function ask(question: string) {
    const trimmed = question.trim()
    if (!trimmed || thinking) return
    setError(null)
    setInput('')
    const userMessage: AssistantMessage = {
      id: `msg-user-${Date.now()}`,
      role: 'user',
      text: trimmed,
      createdAt: new Date().toISOString(),
      citations: [],
    }
    setMessages((prev) => [...prev, userMessage])
    setThinking(true)
    try {
      const answer = await askAssistant(trimmed, assistantContext)
      setMessages((prev) => [...prev, answer])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The assistant could not answer that')
    } finally {
      setThinking(false)
    }
  }

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')
  const chips = lastAssistant?.suggestions ?? STARTERS

  return (
    <div className="space-y-6">
      <Card
        title="Planning assistant"
        subtitle={`Answers from your aggregated calendar — ${events.length} events, ${windows.length} open windows, ${conflicts.length} conflicts in scope`}
      >
        <div className="space-y-4 max-h-[460px] overflow-y-auto pr-1">
          {messages.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-400 space-y-2">
              <p>
                Ask about anything on the family calendar — what is scheduled, who is double-booked, when there is room
                for a trip, or which tracked deals actually fit the dates.
              </p>
              {isPlannerOffline() && (
                <p className="text-xs">
                  The planner API is not configured, so answers come from the offline rules engine reading the events on
                  screen rather than from Claude. It reports only what is actually on the calendar.
                </p>
              )}
            </div>
          )}

          {messages.map((m) => (
            <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div
                className={`max-w-[85%] rounded-xl px-4 py-3 ${
                  m.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700'
                }`}
              >
                {m.role === 'user' ? (
                  <p className="text-sm">{m.text}</p>
                ) : (
                  <>
                    <AnswerText text={m.text} />
                    {m.citations.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <span className="text-slate-400 dark:text-slate-500 text-xs">from:</span>
                        {m.citations.slice(0, 6).map((c, i) => (
                          <Chip key={`${m.id}-cite-${i}`} tone="slate">
                            {c.label}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          {thinking && (
            <div className="flex justify-start">
              <div className="bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3">
                <div className="flex gap-1">
                  {[0, 150, 300].map((d) => (
                    <span
                      key={d}
                      className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
                      style={{ animationDelay: `${d}ms` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {error && <p className="text-red-600 dark:text-red-400 text-sm mt-3">{error}</p>}

        <div className="flex flex-wrap gap-2 mt-4">
          {chips.map((s) => (
            <button
              key={s}
              onClick={() => ask(s)}
              disabled={thinking}
              className="px-3 py-1.5 rounded-full text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>

        <form
          className="flex gap-2 mt-3"
          onSubmit={(e) => {
            e.preventDefault()
            ask(input)
          }}
        >
          <input
            className={inputClass}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about the calendar, conflicts, free weekends, or deals…"
          />
          <button type="submit" className={primaryBtn} disabled={thinking || !input.trim()}>
            Ask
          </button>
        </form>
      </Card>
    </div>
  )
}
